import type { FC } from 'react';
import React, { useCallback, useState, useEffect, useContext } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { ExperienceName } from '@confluence/experience-tracker/entry-points/ExperienceNameType';
import {
	ARCHIVE_PAGES_EXPERIENCE,
	RESTORE_PAGES_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';

// eslint-disable-next-line import/no-cycle
import type { ArchivePollStatusUpdateParams } from './ArchivePollStatus';
import { ArchivePollStatus } from './ArchivePollStatus';
import { ArchiveProcessSuccess } from './ArchiveProcessSuccess';
import { ArchiveFlags } from './ArchiveFlags';
import { ArchivePagesEndAnalytics } from './ArchivePagesEndAnalytics';
import { ArchivePagesLoaderSuccess } from './ArchivePagesLoaderSuccess';
import { UnarchiveSuccessRefreshPageTree } from './UnarchiveSuccessRefreshPageTree';
import { PageStatusFragment } from './PageStatusFragment.fragment';
import type { ArchiveStatusFlagI18n } from './archivingConstants';

export function getExperienceName(isArchive: boolean): ExperienceName {
	return isArchive ? ARCHIVE_PAGES_EXPERIENCE : RESTORE_PAGES_EXPERIENCE;
}

type ArchiveProgressReportProps = {
	longTaskId?: string;
	contentId?: string;
	onSuccess?(): void;
	isArchive?: boolean;
	isBulk: boolean;
	successLinkHref: string;
	source: string;
	error?: Error;
	onSuccessLinkClick?(): void;
	pageCount?: number;
	spaceId?: string | null;
	additionalTrackEventAttributes?: object;
	shouldUseAutomationDiscovery?: boolean;
	flagI18n: ArchiveStatusFlagI18n;
	featureName: string;
};

export const ArchiveProgressReport: FC<ArchiveProgressReportProps> = ({
	longTaskId,
	contentId,
	onSuccess,
	isArchive = true,
	isBulk,
	successLinkHref,
	source,
	error,
	onSuccessLinkClick,
	pageCount,
	spaceId,
	additionalTrackEventAttributes,
	shouldUseAutomationDiscovery,
	flagI18n,
	featureName,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [processedPageIds, setProcessedPageIds] = useState<string[]>([]);
	const [percentageComplete, setPercentageComplete] = useState<number>(0);
	const [archivePollStatusError, setArchivePollStatusError] = useState<Error | undefined>(
		undefined,
	);

	const { getHref, matchRoute } = useContext(RoutesContext);
	const route = matchRoute(getHref());

	/**
	 * Get the status of the page being viewed BEFORE archiving.
	 * If you're already viewing an archived page, you don't need to be redirected on archive success.
	 */
	const pageBeingViewedId = route?.params.contentId || '';
	const [pageBeingViewedLastStatus, setPageBeingViewedLastStatus] = useState<string>('');
	const client = useApolloClient();
	useEffect(() => {
		if (!pageBeingViewedLastStatus && pageBeingViewedId) {
			try {
				const pageBeingViewedFragment = client.readFragment({
					id: `Content:${pageBeingViewedId}`,
					fragment: PageStatusFragment,
				});
				setPageBeingViewedLastStatus(pageBeingViewedFragment?.status || '');
			} catch (e) {}
		}
	}, [client, pageBeingViewedId, pageBeingViewedLastStatus, setPageBeingViewedLastStatus]);

	const handleStatusUpdate = useCallback(
		(pollStatusUpdate: ArchivePollStatusUpdateParams) => {
			const updatedPageIds = sortBy(pollStatusUpdate.archivingSuccessPages);
			const updatedPercentageComplete = pollStatusUpdate.percentageComplete;

			// (Only set the pageIds if they have changed, as React's state comparison thinks two
			// different object instances with the same members and values are different, and causes an
			// infinite rendering loop. Note we don't need to check primitive values like
			// `percentageComplete` because React correctly compares them.)
			isEqual(processedPageIds, updatedPageIds) || setProcessedPageIds(updatedPageIds);
			setPercentageComplete(updatedPercentageComplete);

			// only call onSuccess if percent complete is 100.
			if (updatedPercentageComplete === 100 && onSuccess) {
				if (isArchive && isBulk) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'succeeded',
							actionSubject: 'bulkArchive',
							source: featureName,
							attributes: {
								pageCount: updatedPageIds.length,
							},
						},
					}).fire();
				}

				onSuccess();
			}
		},
		[onSuccess, processedPageIds, isArchive, isBulk, createAnalyticsEvent, featureName],
	);

	const archivingError = error || archivePollStatusError;

	// When archived remotely via archiving suggestion email, BE does not pass in contentIds.
	// And with bulk archiving, since there are multiple contentIds which may or may
	// not have a common ancestor, no single representing contentId is passed into this component.
	// Hence, we may need to derive contentId from processedPageIds. The single ID is somewhat
	// arbitrary at this point, just used for analytics and refreshing a few queries.
	// TODO: Remove once BE provide contentIds in query parameters
	const contentIdOrProcessedId = contentId || processedPageIds[0];

	return (
		<>
			{archivingError ? (
				<ErrorDisplay error={archivingError} />
			) : percentageComplete === 100 ? (
				<>
					<ArchiveProcessSuccess
						contentId={contentIdOrProcessedId}
						processedPageIds={processedPageIds}
					/>
					<ExperienceSuccess name={getExperienceName(isArchive)} attributes={{ featureName }} />
					{isArchive ? (
						<ArchivePagesLoaderSuccess
							contentId={contentIdOrProcessedId}
							pageBeingViewedId={pageBeingViewedId}
							pageBeingViewedLastStatus={pageBeingViewedLastStatus}
						/>
					) : (
						<UnarchiveSuccessRefreshPageTree contentId={contentIdOrProcessedId} />
					)}
				</>
			) : longTaskId ? (
				<ArchivePollStatus
					processedPageIds={processedPageIds}
					longTaskId={longTaskId}
					onError={setArchivePollStatusError}
					onStatusUpdate={handleStatusUpdate}
					isArchive={isArchive}
					isBulk={isBulk}
					featureName={featureName}
				/>
			) : null}
			<ArchiveFlags
				contentIds={processedPageIds}
				percentageComplete={percentageComplete}
				error={archivingError}
				flagI18n={flagI18n}
				isArchive={isArchive}
				isIndeterminate
				successLinkHref={successLinkHref}
				onSuccessLinkClick={onSuccessLinkClick}
				isBulk={isBulk}
				shouldUseAutomationDiscovery={shouldUseAutomationDiscovery}
			/>
			<ArchivePagesEndAnalytics
				archivingError={archivingError}
				archivingPercentageComplete={percentageComplete}
				isBulk={isBulk}
				contentIds={isBulk ? processedPageIds : [contentIdOrProcessedId]}
				isArchive={isArchive}
				source={source}
				pageCount={pageCount}
				spaceId={spaceId}
				additionalTrackEventAttributes={additionalTrackEventAttributes}
			/>
		</>
	);
};
